import { getProduct } from './Product';
import { isListView } from './Experiments';
import { getAspectRatioBasedOnRowProducts, HOME_LAYOUT_TYPE } from './Utils';

const body = document.body;
const html = document.documentElement;
let recalculateTimeout = null;

const viewProductListeners = [];
const productTiles = {};
const tileHeights = {};
let galleryProductId = null;

export const getDocumentHeight = () =>
  Math.max(
    body.scrollHeight,
    body.offsetHeight,
    html.clientHeight,
    html.scrollHeight,
    html.offsetHeight
  );

export const getScrollTop = (scrollElement) =>
  (scrollElement || {}).scrollTop ||
  (window.scrollingElement || {}).scrollTop ||
  (window.scrollableElement || {}).scrollTop ||
  window.scrollTop ||
  window.scrollY;

export const recalculate = () => {
  if (recalculateTimeout) {
    clearTimeout(recalculateTimeout);
    recalculateTimeout = null;
  }

  recalculateTimeout = setTimeout(() => {
    Array.prototype.forEach.call(
      document.querySelectorAll('.home-product-tile-container'),
      (tile) => {
        const tileId = tile.id;
        const rect = tile.getBoundingClientRect();

        productTiles[tileId] = {
          element: tile,
          width: rect.width,
          height: rect.height,
          offsetTop: tile.parentNode ? tile.parentNode.offsetTop + 70 : 70,
          get top () {
            return this.offsetTop - getScrollTop();
          },
          get bottom () {
            return this.offsetTop + this.height - getScrollTop();
          }
        };
      }
    );
  }, 100);
};

export const addViewProductListener = (callback) => {
  viewProductListeners.push(callback);
};

export const removeViewProductListener = (callback) => {
  const pos = viewProductListeners.indexOf(callback);
  if (pos !== -1) {
    viewProductListeners.splice(pos, 1);
  }
};

export const notifyViewProductListeners = () => {
  viewProductListeners.forEach((callback) => {
    callback();
  });
};

export const setTileHeight = (id, height, fromCustomPage, sectionId, customPageId) => {
  const key = fromCustomPage ? `${id}_${sectionId}_${customPageId}` : id;
  tileHeights[key] = height;
};

const calculateListViewHeight = (id) => {
  if (
    !id ||
    id === 'DESCRIPTION' ||
    id === 'FOOTER' ||
    id === 'TAGS_SELECTOR' ||
    id === 'TAGS_INFO' ||
    id === 'CATALOGUE_INFO' ||
    id === 'HOW_TO_ORDER'
  ) {
    return 200;
  }

  if (id === 'VIEW_OFFERS') {
    return 50;
  }

  if (id === 'COMPANY_BANNER') {
    return 66;
  }

  if (id === 'NEXT_PAGE_LOADER' || id === 'PREV_PAGE_LOADER' || id === 'MORE-CATALOGUES') {
    return 0;
  }

  return LIST_VIEW_IMAGE_DEFAULT_MIN_HEIGHT;
};

const calculateGridTileHeight = (id, productIds, fromCustomPage) => {
  if (
    !id ||
    id === 'DESCRIPTION' ||
    id === 'FOOTER' ||
    id === 'TAGS_SELECTOR' ||
    id === 'TAGS_INFO' ||
    id === 'CATALOGUE_INFO' ||
    id === 'HOW_TO_ORDER'
  ) {
    return 200;
  }

  if (id === 'VIEW_OFFERS') {
    return 50;
  }

  if (id === 'COMPANY_BANNER') {
    return 66;
  }

  if (
    id === 'CATALOGUE_BANNER' ||
    id === 'NEXT_PAGE_LOADER' ||
    id === 'PREV_PAGE_LOADER' ||
    id === 'MORE-CATALOGUES'
  ) {
    return 0;
  }

  const product = getProduct(id, fromCustomPage);

  if (!product) {
    return 300;
  }

  const aspectRatio = getAspectRatioBasedOnRowProducts(productIds, fromCustomPage);

  const windowWidth = window.innerWidth;
  let itemWidthPercent = 50; // percent
  if (windowWidth >= 1025) {
    itemWidthPercent = 20;
  } else if (windowWidth >= 769) {
    itemWidthPercent = 30;
  } else if (windowWidth >= 668) {
    itemWidthPercent = 33.3;
  }

  const itemWidth = (window.innerWidth * itemWidthPercent) / 100 - 10;

  const wrapperHeight = itemWidth / aspectRatio + 28 + 20 + 20 + 10;

  return wrapperHeight;
};

export const getTileHeight = (id, productIds, fromCustomPage, sectionId, customPageId) => {
  const key = fromCustomPage ? `${id}_${sectionId}_${customPageId}` : id;
  if (tileHeights[key] !== null && tileHeights[key] !== undefined) {
    return tileHeights[key];
  }

  if (isListView()) {
    tileHeights[key] = calculateListViewHeight(id);
  } else {
    const height = calculateGridTileHeight(id, productIds, fromCustomPage);
    tileHeights[key] = height;

    if (Array.isArray(productIds)) {
      productIds.forEach((productId) => {
        tileHeights[fromCustomPage ? `${productId}_${sectionId}_${customPageId}` : productId] =
          height;
      });
    }
  }

  return tileHeights[key];
};

export const MAGAZINE_DISCOUNT_BADGE_HEIGHT = 40;
export const MAGAZINE_PHOTOS_OPTIONS_HEIGHT = 85;
export const MAGAZINE_SKU_HEIGHT = 25;
export const MAGAZINE_PRICE_HEIGHT = 22;
export const MAGAZINE_TAXED_PRICE_HEIGHT = 22;
export const MAGAZINE_MOQ_HEIGHT = 22;
export const MAGAZINE_SIZE_AND_COLOR_OPTIONS_HEIGHT = 25;
export const MAGAZINE_LAYOUT_VERTICAL_PADDING = 20; // 10px padding each top-bottom
export const MAGAZINE_LOADER_SIZE = 40;
export const MAGAZINE_ADD_TO_LIST_HEIGHT = 45;

export const GRID_PRICE_HEIGHT = 23;
export const GRID_TAXED_PRICE_HEIGHT = 20;
export const GRID_NAME_HEIGHT = 20;
export const GRID_DETAILS_VERTICAL_SPACING = 12;
export const GRID_LOADER_SIZE = 40;
export const GRID_ADD_TO_LIST_HEIGHT = 40;
export const GRID_COLOR_OPTIONS_HEIGHT = 21;
export const GRID_VIEW_VERTICAL_MARGIN = 10;
export const CUSTOM_PAGE_VIEW_VERTICAL_MARGIN = 20;
export const GRID_VIEW_HORIZONTAL_SPACING = 10;
export const GRID_MIN_IMAGE_HEIGHT = 60;
export const GRID_MAX_IMAGE_HEIGHT = 250;

export const LIST_VIEW_ADD_TO_LIST_HEIGHT = 30;
export const LIST_VIEW_DISCOUNT_BADGE_HEIGHT = 26;
export const LIST_VIEW_VERTICAL_MARGIN = 10;
export const LIST_VIEW_VERTICAL_PADDING = 20;
export const LIST_VIEW_SPACE_BETWEEN_TILES = 10;
export const LIST_VIEW_IMAGE_DEFAULT_MAX_WIDTH = 96;
export const LIST_VIEW_IMAGE_DEFAULT_MIN_HEIGHT = 64;
export const LIST_VIEW_IMAGE_DEFAULT_MAX_HEIGHT = 144;

export const GRID_VIEW_CATALOGUE_LIST_HORIZONTAL_PADDING = 20;
export const GRID_VIEW_CATALOGUE_CONTAINER_HORIZONTAL_PADDING = 20;

export const MORE_CATALOGUES_LOADER_CONTAINER_HEIGHT = 250;

export const setGalleryProductId = (productId) => {
  galleryProductId = productId;
};

export const getGalleryProductId = () => galleryProductId;

export const getCatalogueViewActionButtonHeight = ({ homeLayoutType }) => {
  if (homeLayoutType === HOME_LAYOUT_TYPE.MAGAZINE) {
    return MAGAZINE_ADD_TO_LIST_HEIGHT;
  }
  if (homeLayoutType === HOME_LAYOUT_TYPE.LIST) {
    return LIST_VIEW_ADD_TO_LIST_HEIGHT;
  }

  return GRID_ADD_TO_LIST_HEIGHT;
};
